import {
  buildClassicWorkspaceEntryPath,
  CHOOSE_ORGANIZATION_PATH,
} from "../../core/lib/classic-path"

/**
 * `hasOrganizationId` lives in `core` because the classic console asks the same
 * question and `core` must not import from `dashboard`. Re-exported here so the
 * dashboard modules that already import it from this file keep working.
 */
export { hasOrganizationId } from "../../core/lib/organization-id"

/**
 * Where the "pick an organization to continue" fallback lives as a route. Also
 * defined in `core` for the same reason as `hasOrganizationId`.
 */
export { CHOOSE_ORGANIZATION_PATH }

export interface DashboardPathArgs {
  organizationId: string
  workspaceId: string
  /** Path below the workspace root, with or without a leading slash. */
  subpath?: string
  /** Query string, with or without a leading "?". */
  search?: string
  /** URL fragment, with or without a leading "#". */
  hash?: string
}

/**
 * Builds an org-scoped dashboard URL. Pure and framework-free so both the App
 * Router dashboard and the Pages Router console can use it.
 */
export function buildDashboardPath({
  organizationId,
  workspaceId,
  subpath = "",
  search = "",
  hash = "",
}: DashboardPathArgs): string {
  const normalizedSubpath =
    subpath === "" || subpath === "/"
      ? ""
      : subpath.startsWith("/")
      ? subpath
      : `/${subpath}`

  const normalizedSearch =
    search === "" || search === "?"
      ? ""
      : search.startsWith("?")
      ? search
      : `?${search}`

  const normalizedHash =
    hash === "" || hash === "#" ? "" : hash.startsWith("#") ? hash : `#${hash}`

  // The ids are single path segments, so they're encoded. `subpath`, `search`
  // and `hash` are already-formed URL parts and are passed through untouched.
  const org = encodeURIComponent(organizationId)
  const workspace = encodeURIComponent(workspaceId)

  return `/dashboard/organization/${org}/workspace/${workspace}${normalizedSubpath}${normalizedSearch}${normalizedHash}`
}

/**
 * The current URL fragment, or "" outside the browser. Client-only concern:
 * `usePathname`/`useSearchParams` never see the fragment, so any redirect that
 * wants to preserve it has to read `window.location` directly.
 */
export function getCurrentLocationHash(): string {
  return typeof window === "undefined" ? "" : window.location.hash
}

/**
 * The org landing page, which resolves a workspace inside the organization and
 * forwards to its dashboard. Use it whenever the organization is known but the
 * workspace is not.
 */
export function buildOrganizationLandingPath(organizationId: string): string {
  return `/dashboard/organization/${encodeURIComponent(organizationId)}`
}

/**
 * Where to land when the user picks a workspace. A workspace that doesn't
 * belong to an organization has no org-scoped dashboard URL, so it opens in
 * the classic console instead of a dashboard URL that would bounce back to
 * `/dashboard`.
 */
export function buildWorkspaceEntryPath({
  organizationId,
  workspaceId,
}: {
  organizationId: string | null | undefined
  workspaceId: string
}): string {
  if (organizationId == null || organizationId === "") {
    return buildClassicWorkspaceEntryPath({
      organizationId,
      workspaceId,
      subpath: "/devices",
    })
  }
  return buildDashboardPath({ organizationId, workspaceId })
}
